import { Eye, Repeat } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CampaignStageCadences } from "@/features/campaign-stage-cadences";
import { ApiError } from "@/lib/api";
import {
  BUCKETS, bucketText, nextReasonText, positionText, priorityReasonText, relativeSpan,
} from "@/lib/cadence";
import { dialText } from "@/lib/contacts";
import { leadName } from "@/lib/campaign";
import { cadenceNoCalls } from "@/lib/no-calls";
import { useCadenceMigratePreview, useCadenceWork } from "@/lib/queries";
import type { Campaign, CadenceMigrate } from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { cn, formatDateTime, formatPhoneNumber } from "@/lib/utils";

/**
 * The campaign's work under its cadence (ADR-066): the buckets, the rows of one, and the reps
 * free with nothing to present. Read as the dialer reads it, every half minute. A supervisor
 * sees what a lead is waiting for and why; the controls that change a lead are on the lead's
 * record, and the cadence itself is picked on the Dialing tab. The stages that run a cadence of
 * their own (ADR-087) are the table between the two (`campaign-stage-cadences.tsx`).
 */
export function CampaignCadence({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const [bucket, setBucket] = React.useState<string>("due");
  const work = useCadenceWork(campaign.id, bucket);
  const now = useNow(30_000);
  const [preview, setPreview] = React.useState<CadenceMigrate | null>(null);
  const migrate = useCadenceMigratePreview();

  if (!campaign.cadence_id) {
    return (
      <EmptyState
        icon={Repeat}
        title="No cadence on this campaign"
        description="A campaign that dials runs a cadence: it says how often each lead is called, and in what order. Pick one on the Dialing tab."
        action={
          <Button variant="outline" asChild>
            <Link to={`/campaigns/${campaign.id}?tab=dialing`}>Open Dialing</Link>
          </Button>
        }
      />
    );
  }

  const data = work.data;
  const counts = new Map((data?.buckets ?? []).map((b) => [b.bucket, b.count]));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={campaign.cadence?.name ?? "Cadence"}
          description={
            <span className="inline-flex flex-wrap items-center gap-2">
              {campaign.cadence?.version_n && <span className="font-mono text-xs">v{campaign.cadence.version_n}</span>}
              <span>The campaign's cadence: every lead on the list runs it unless their stage names another below. Picked on the Dialing tab.</span>
            </span>
          }
          action={
            canManage && (
              <Button
                variant="outline"
                size="sm"
                loading={migrate.isPending}
                onClick={() =>
                  migrate.mutate(campaign.id, {
                    onSuccess: setPreview,
                  })
                }
              >
                <Eye />
                Preview positions
              </Button>
            )
          }
        />
        {(campaign.cadence_warnings?.length || campaign.cadence?.problem || migrate.error) && (
          <CardBody className="space-y-2">
            {campaign.cadence?.problem && <Callout tone="danger">{campaign.cadence.problem}</Callout>}
            {(campaign.cadence_warnings ?? []).map((w) => (
              <Callout key={w} tone="warning">{w}</Callout>
            ))}
            {migrate.error && (
              <Callout tone="danger">{migrate.error instanceof ApiError ? migrate.error.message : "Could not preview."}</Callout>
            )}
          </CardBody>
        )}
      </Card>

      <CampaignStageCadences campaign={campaign} canManage={canManage} />

      <div className="grid gap-2 sm:grid-cols-4">
        {BUCKETS.map((name) => {
          const term = bucketText(name);
          const active = bucket === name;
          const n = counts.get(name) ?? 0;
          return (
            <button
              key={name}
              type="button"
              onClick={() => setBucket(name)}
              title={term.description}
              className={cn(
                "rounded-md border px-3 py-2 text-left transition-colors",
                active ? "border-accent-8 bg-accent-2" : "border-border bg-surface hover:bg-surface-hover",
              )}
            >
              <span className="block text-xs text-text-muted">{term.label}</span>
              <span className={cn("block text-lg font-semibold tabular-nums", n > 0 && term.tone === "danger" ? "text-danger-11" : "text-text")}>
                {data ? n : "—"}
              </span>
            </button>
          );
        })}
      </div>

      <Card className="overflow-hidden">
        <CardHeader
          title={bucketText(bucket).label}
          description={`${bucketText(bucket).description} What the dialer will do next for each lead, refreshed every half minute.`}
          action={data && <span className="text-xs text-text-muted">{`${data.rows.length} shown`}</span>}
        />
        {work.isPending ? (
          <TableSkeleton cols={6} rows={4} />
        ) : work.error ? (
          <ErrorState error={work.error} title="Could not load the work" action={<Button variant="outline" onClick={() => void work.refetch()}>Try again</Button>} />
        ) : data && data.rows.length === 0 ? (
          <EmptyState title={`Nothing ${bucketText(bucket).label.toLowerCase()}`} description="Pick another bucket above." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Stands</TableHead>
                  <TableHead>Next</TableHead>
                  <TableHead>Why</TableHead>
                  <TableHead className="text-right">Priority</TableHead>
                  <TableHead>Position</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.rows ?? []).map((row) => {
                  const dial = dialText(row.dial ?? row.state);
                  const name = leadName(row);
                  return (
                    <TableRow key={row.campaign_lead_id}>
                      <TableCell>
                        <Link to={`/contacts/${row.lead_id}`} className="block text-sm text-text hover:underline">
                          {name ?? "No name"}
                        </Link>
                        <span className="block font-mono text-xs text-text-muted">{formatPhoneNumber(row.phone)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge tone={dial.tone} size="sm" title={dial.description}>{dial.label}</Badge>
                        {row.hold_reason && row.hold_until && new Date(row.hold_until).getTime() > now && (
                          <span className="mt-0.5 block text-xs text-text-muted">held: {row.hold_reason}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-text-muted">
                        {row.next_attempt_at ? (
                          <span title={formatDateTime(row.next_attempt_at)}>{relativeSpan(row.next_attempt_at, now)}</span>
                        ) : (
                          "now"
                        )}
                        {row.step_not_after && (
                          <span className="block text-text-subtle" title={formatDateTime(row.step_not_after)}>
                            good until {relativeSpan(row.step_not_after, now)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-text">
                        {nextReasonText(row.next_reason) ?? <span className="text-text-subtle">—</span>}
                        {row.technical_retries > 0 && (
                          <span className="block text-text-muted">retry {row.technical_retries}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        <span className="text-text">{row.priority}</span>
                        <span className="block text-text-muted">{priorityReasonText(row.priority_reason) ?? "—"}</span>
                      </TableCell>
                      <TableCell className="text-xs text-text-muted">
                        {positionText(row.cycle, row.step)}
                        <span className="block text-text-subtle">{row.attempts} attempt{row.attempts === 1 ? "" : "s"}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {data && (data.no_calls_now ?? []).length > 0 && (
        <Card>
          <CardHeader
            title="Ready reps who get no calls now"
            description="What each rep's phone says about this campaign, from the dialer's last look for a lead for them. A rep leaves the list once the dialer gives them one."
          />
          <CardBody className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rep</TableHead>
                  <TableHead>Why</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.no_calls_now ?? []).map((row) => {
                  const why = cadenceNoCalls(row, new Date(now));
                  return (
                    <TableRow key={row.rep}>
                      <TableCell className="font-mono text-xs">{row.rep}</TableCell>
                      <TableCell className={cn("text-xs", why.tone === "warning" ? "text-warning-11" : "text-text")}>
                        {why.text}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      )}

      {data && data.dial_problems.length > 0 && (
        <Card>
          <CardHeader title="Reps free with nothing to dial out on" description="A rep was Ready and the dialer placed nothing for them: no trunk, no number to present, or the campaign stopped at its abandon limit (see the Dialing tab). The row counts every minute it went on." />
          <CardBody className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rep</TableHead>
                  <TableHead>Problem</TableHead>
                  <TableHead>First seen</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Times</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.dial_problems.map((p) => (
                  <TableRow key={`${p.rep}-${p.reason}`}>
                    <TableCell className="font-mono text-xs">{p.rep}</TableCell>
                    <TableCell className="text-xs">{p.reason.replaceAll("_", " ")}</TableCell>
                    <TableCell className="text-xs text-text-muted">{formatDateTime(p.first_seen)}</TableCell>
                    <TableCell className="text-xs text-text-muted">{formatDateTime(p.last_seen)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      )}

      <Dialog open={preview !== null} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Where each lead stands</DialogTitle>
            <DialogDescription>
              A preview: the position and priority the dialer will read each row at its next move. Nothing is written
              here. {preview && preview.total > preview.rows.length ? `The first ${preview.rows.length} of ${preview.total}.` : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Row</TableHead>
                  <TableHead>Stands</TableHead>
                  <TableHead className="text-right">Attempts</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Phase</TableHead>
                  <TableHead className="text-right">Priority</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(preview?.rows ?? []).map((row) => (
                  <TableRow key={row.campaign_lead_id}>
                    <TableCell>
                      <Link to={`/contacts/${row.lead_id}`} className="text-xs hover:underline">#{row.campaign_lead_id}</Link>
                      {!row.on_cadence && <span className="ml-1 text-xs text-text-subtle">(not yet on it)</span>}
                    </TableCell>
                    <TableCell className="text-xs">{row.state}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{row.attempts}</TableCell>
                    <TableCell className="text-xs">{positionText(row.cycle, row.step)}</TableCell>
                    <TableCell className="text-xs">{row.phase ?? "past the end"}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      {row.priority}
                      <span className="block text-text-muted">{priorityReasonText(row.priority_reason) ?? "—"}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreview(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
